import { useEffect, useRef, useState, type CSSProperties, type JSX, type ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  EXPENSE_CATEGORIES, EXPENSE_STATUS_LABELS, PAY_METHODS, expenseTotals,
  type BusinessTrip, type ExpenseCategory, type ExpenseLine, type ExpenseStatus, type ExpenseSummary,
} from '@dxtech/shared';
import { api } from '../api/client.js';
import { expenseApi } from '../api/expense.js';
import { useApp } from '../state/app.js';
import { useIsMobile } from '../lib/useIsMobile.js';
import { ConfirmDialog, ModalFrame, Spinner } from '../ui/common.js';
import { AddRegular } from '@fluentui/react-icons';

const MAX_BYTES = 50 * 1024 * 1024;
export const won = (n: number) => `${n.toLocaleString('ko-KR')}원`;
const todayKst = () => new Date(Date.now() + 9 * 3600 * 1000).toISOString().slice(0, 10);
const md = (d?: string) => (d ? `${+d.slice(5, 7)}/${+d.slice(8, 10)}` : '');
const dt = (iso?: string) => (iso ? new Date(iso).toLocaleString('ko-KR', { timeZone: 'Asia/Seoul', month: 'numeric', day: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false }) : '');
const period = (s: { firstDate: string; lastDate: string }) => (s.firstDate ? `${md(s.firstDate)}${s.lastDate !== s.firstDate ? ` ~ ${md(s.lastDate)}` : ''}` : '—');
const errMsg = (e: unknown, f: string) => (e instanceof Error ? e.message : f);
const readB64 = (file: File) => new Promise<string>((resolve, reject) => {
  const fr = new FileReader();
  fr.onload = () => resolve(String(fr.result).split(',')[1] ?? '');
  fr.onerror = () => reject(new Error('파일 읽기 실패'));
  fr.readAsDataURL(file);
});

const STATUS_STYLE: Record<ExpenseStatus, CSSProperties> = {
  draft: { background: '#F3F4F6', color: '#6B7280' },
  submitted: { background: '#DBEAFE', color: '#1D4ED8' },
  approved: { background: '#FEF3C7', color: '#B45309' },
  rejected: { background: '#FEE2E2', color: '#B91C1C' },
  done: { background: '#DCFCE7', color: '#15803D' },
};
export const ExpenseBadge = ({ status }: { status: ExpenseStatus }) => (
  <span style={{ ...STATUS_STYLE[status], fontSize: 11.5, fontWeight: 700, padding: '3px 9px', borderRadius: 999, whiteSpace: 'nowrap' }}>{EXPENSE_STATUS_LABELS[status]}</span>
);
const Kpi = ({ label, value, sub }: { label: string; value: string; sub?: string }) => (
  <div className="dx-card" style={{ padding: '14px 16px', flex: '1 1 160px' }}>
    <div style={{ fontSize: 12, color: '#6B7280' }}>{label}</div>
    <div style={{ fontSize: 20, fontWeight: 800, marginTop: 4 }}>{value}</div>
    {sub && <div style={{ fontSize: 11.5, color: '#9CA3AF', marginTop: 2 }}>{sub}</div>}
  </div>
);

type Tab = 'mine' | 'approve' | 'all';

// 경비지출 — 개인 지출·법인카드 사용 청구, CEO 단일 결재(지급 확인도 CEO)
export function Expense(): JSX.Element {
  const { me } = useApp();
  const ceo = me.roles.includes('CEO');
  const viewAll = ceo || me.roles.includes('Admin');
  const [tab, setTab] = useState<Tab>('mine');
  const [modal, setModal] = useState<{ id?: string } | null>(null);
  const mine = useQuery({ queryKey: ['expenses', 'mine'], queryFn: () => expenseApi.list('mine') });
  const all = useQuery({ queryKey: ['expenses', 'all'], queryFn: () => expenseApi.list('all'), enabled: viewAll });
  const pending = (all.data?.items ?? []).filter((s) => s.status === 'submitted' || s.status === 'approved');

  // CEO=결재함(승인·지급 처리), 관리자=결재 진행(같은 목록, 조회만)
  const tabs: Array<[Tab, string, number?]> = [['mine', '내 경비'], ...(viewAll ? [['approve', ceo ? '결재함' : '결재 진행', pending.length] as [Tab, string, number], ['all', '경비 현황'] as [Tab, string]] : [])];

  return (
    <div style={{ maxWidth: 1080, margin: '0 auto', animation: 'dxfade .3s ease', display: 'flex', flexDirection: 'column', gap: 16 }}>
      {/* 화면 제목은 상단바에 있으므로 안내 한 줄 + 주요 동작만 */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <div style={{ fontSize: 13, color: '#6B7280' }}>개인 지출과 법인카드 사용을 영수증과 함께 청구합니다. 결재와 지급 확인은 대표이사가 합니다.</div>
        <button className="dx-btn dx-btn-primary" style={{ marginLeft: 'auto', fontSize: 13 }} onClick={() => setModal({})}><AddRegular aria-hidden style={{ fontSize: 16 }} />새 청구</button>
      </div>

      {tabs.length > 1 && (
        <div style={{ display: 'flex', gap: 4, borderBottom: '1px solid #E5E7EB' }}>
          {tabs.map(([t, label, n]) => (
            <button key={t} onClick={() => setTab(t)}
              style={{ border: 'none', background: 'transparent', fontFamily: 'inherit', fontSize: 13.5, fontWeight: 700, padding: '9px 14px', cursor: 'pointer', color: tab === t ? '#1D4ED8' : '#6B7280', borderBottom: `2px solid ${tab === t ? '#2563EB' : 'transparent'}`, marginBottom: -1 }}>
              {label}{n ? <span style={{ marginLeft: 6, background: '#EF4444', color: '#fff', fontSize: 11, borderRadius: 9, padding: '1px 6px' }}>{n}</span> : null}
            </button>
          ))}
        </div>
      )}

      {tab === 'mine' && <MineTab q={mine} onOpen={(id) => setModal({ id })} />}
      {tab === 'approve' && <ClaimList items={pending} loading={all.isLoading} showOwner empty={ceo ? "결재·지급할 청구서가 없습니다." : "결재·지급 진행 중인 청구서가 없습니다."} onOpen={(id) => setModal({ id })} />}
      {tab === 'all' && <AllTab items={all.data?.items ?? []} loading={all.isLoading} onOpen={(id) => setModal({ id })} />}

      {modal && <ExpenseModal id={modal.id} onClose={() => setModal(null)} />}
    </div>
  );
}

function MineTab({ q, onOpen }: { q: { data?: { items: ExpenseSummary[] }; isLoading: boolean }; onOpen: (id: string) => void }): JSX.Element {
  const items = q.data?.items ?? [];
  const month = todayKst().slice(0, 7);
  const thisMonth = items.filter((s) => s.month === month && s.status !== 'draft');
  return (
    <>
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
        <Kpi label={`${+month.slice(5)}월 청구액`} value={won(thisMonth.reduce((a, s) => a + s.total, 0))} sub={`${thisMonth.length}건 (작성중 제외)`} />
        <Kpi label="결재대기" value={`${items.filter((s) => s.status === 'submitted').length}건`} />
        <Kpi label="환급 예정(지급대기)" value={won(items.filter((s) => s.status === 'approved').reduce((a, s) => a + s.personal, 0))} />
      </div>
      <ClaimList items={items} loading={q.isLoading} empty="청구 내역이 없습니다. 오른쪽 위 ‘새 청구’로 작성하세요." onOpen={onOpen} />
    </>
  );
}

function ClaimList({ items, loading, showOwner, empty, onOpen }: { items: ExpenseSummary[]; loading: boolean; showOwner?: boolean; empty: string; onOpen: (id: string) => void }): JSX.Element {
  return (
    <div className="dx-card" style={{ overflow: 'hidden' }}>
      {loading ? <div style={{ padding: 32, textAlign: 'center' }}><Spinner size={22} /></div>
        : items.length === 0 ? <div style={{ padding: 32, textAlign: 'center', color: '#9CA3AF', fontSize: 13 }}>{empty}</div>
        : items.map((s) => (
          <button key={s.id} onClick={() => onOpen(s.id)}
            style={{ display: 'flex', alignItems: 'center', gap: 12, width: '100%', border: 'none', borderBottom: '1px solid #F3F4F6', background: '#fff', padding: '12px 18px', textAlign: 'left', fontFamily: 'inherit', cursor: 'pointer', flexWrap: 'wrap' }}>
            <div style={{ width: 84, flexShrink: 0, fontSize: 12.5, color: '#6B7280' }}>{period(s)}</div>
            <div style={{ flex: '1 1 220px', minWidth: 0 }}>
              <div style={{ fontSize: 14, fontWeight: 700, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {showOwner && <span style={{ color: '#2563EB', marginRight: 6 }}>{s.ownerName}</span>}{s.title}
              </div>
              <div style={{ fontSize: 11.5, color: '#9CA3AF', marginTop: 2 }}>
                {[s.project, s.tripLabel && `출장 ${s.tripLabel}`, `${s.lineCount}건`].filter(Boolean).join(' · ')}
              </div>
            </div>
            <div style={{ textAlign: 'right', flexShrink: 0 }}>
              <div style={{ fontSize: 14, fontWeight: 800 }}>{won(s.total)}</div>
              <div style={{ fontSize: 11, color: '#9CA3AF' }}>{s.personal ? `개인 ${won(s.personal)}` : ''}{s.personal && s.card ? ' · ' : ''}{s.card ? `법인카드 ${won(s.card)}` : ''}</div>
            </div>
            <ExpenseBadge status={s.status} />
          </button>
        ))}
    </div>
  );
}

const STATUS_ORDER: ExpenseStatus[] = ['draft', 'submitted', 'approved', 'rejected', 'done'];

/** 경비 현황(CEO·관리자) — 기간(월별/전체)·상태·검색으로 모든 청구서 조회 */
function AllTab({ items, loading, onOpen }: { items: ExpenseSummary[]; loading: boolean; onOpen: (id: string) => void }): JSX.Element {
  const { flash } = useApp();
  const [month, setMonth] = useState(todayKst().slice(0, 7));
  const [allPeriod, setAllPeriod] = useState(false);
  const [status, setStatus] = useState<ExpenseStatus | 'all'>('all');
  const [query, setQuery] = useState('');
  const inPeriod = allPeriod ? items : items.filter((s) => (s.month || s.createdAt.slice(0, 7)) === month); // 내역 없는 작성중은 작성월
  const kw = query.trim().toLowerCase();
  const list = inPeriod
    .filter((s) => status === 'all' || s.status === status)
    .filter((s) => !kw || [s.ownerName, s.title, s.project, s.tripLabel, s.dept, s.id].some((v) => (v ?? '').toLowerCase().includes(kw)));
  const counted = list.filter((s) => s.status !== 'draft' && s.status !== 'rejected'); // 금액 집계는 제출·진행·완료 건만
  const sum = (f: (s: ExpenseSummary) => number) => counted.reduce((a, s) => a + f(s), 0);
  const byPerson = Object.entries(counted.reduce<Record<string, number>>((m, s) => ({ ...m, [s.ownerName]: (m[s.ownerName] ?? 0) + s.total }), {})).sort((a, b) => b[1] - a[1]);
  const byCat = EXPENSE_CATEGORIES.map((c) => [c, sum((s) => s.byCategory[c] ?? 0)] as const).filter(([, v]) => v > 0).sort((a, b) => b[1] - a[1]);
  const max = Math.max(1, ...byCat.map(([, v]) => v));
  const panel = (title: string, body: ReactNode) => (
    <div className="dx-card" style={{ padding: '14px 16px', flex: '1 1 300px' }}>
      <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 10 }}>{title}</div>{body}
    </div>
  );
  const chip = (key: ExpenseStatus | 'all', text: string, n: number) => {
    const on = status === key;
    return (
      <button key={key} onClick={() => setStatus(key)} aria-pressed={on}
        style={{ border: `1px solid ${on ? '#2563EB' : '#E5E7EB'}`, background: on ? '#EFF4FE' : '#fff', color: on ? '#1D4ED8' : '#4B5563', borderRadius: 999, padding: '5px 12px', fontFamily: 'inherit', fontSize: 12.5, fontWeight: 600, cursor: 'pointer' }}>
        {text} <span style={{ color: on ? '#2563EB' : '#9CA3AF' }}>{n}</span>
      </button>
    );
  };
  const periodBtn = (on: boolean, text: string, onClick: () => void) => (
    <button onClick={onClick} aria-pressed={on}
      style={{ border: 'none', background: on ? '#fff' : 'transparent', boxShadow: on ? '0 1px 2px rgba(16,24,40,.12)' : 'none', color: on ? '#1F2937' : '#6B7280', borderRadius: 6, padding: '5px 12px', fontFamily: 'inherit', fontSize: 12.5, fontWeight: 700, cursor: 'pointer' }}>{text}</button>
  );

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <span style={{ display: 'inline-flex', background: '#F3F4F6', borderRadius: 8, padding: 3 }}>
          {periodBtn(!allPeriod, '월별', () => setAllPeriod(false))}
          {periodBtn(allPeriod, '전체 기간', () => setAllPeriod(true))}
        </span>
        {!allPeriod && (
          <input type="month" className="dx-input" value={month} onChange={(e) => e.target.value && setMonth(e.target.value)} style={{ height: 34, boxSizing: 'border-box' }} aria-label="조회 월" />
        )}
        <input className="dx-input" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="작성자·제목·프로젝트·출장 검색"
          style={{ height: 34, boxSizing: 'border-box', width: 240, maxWidth: '100%' }} aria-label="검색" />
        <button className="dx-btn dx-btn-ghost" style={{ marginLeft: 'auto', fontSize: 12.5, padding: '8px 14px' }} disabled={allPeriod || !inPeriod.length}
          title={allPeriod ? '엑셀 내보내기는 월별로 합니다' : undefined}
          onClick={() => void expenseApi.exportCsv(month).catch((e) => flash(errMsg(e, '내보내기 실패')))}>엑셀(CSV) 내보내기</button>
      </div>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        {chip('all', '전체', inPeriod.length)}
        {STATUS_ORDER.map((st) => chip(st, EXPENSE_STATUS_LABELS[st], inPeriod.filter((s) => s.status === st).length))}
      </div>
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
        <Kpi label="총 사용액" value={won(sum((s) => s.total))} sub={`${counted.length}건 · 작성중·반려 제외`} />
        <Kpi label="개인 지출(환급)" value={won(sum((s) => s.personal))} sub={`미지급 ${won(counted.filter((s) => s.status === 'approved').reduce((a, s) => a + s.personal, 0))}`} />
        <Kpi label="법인카드" value={won(sum((s) => s.card))} />
      </div>
      {counted.length > 0 && (
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          {panel('분류별', byCat.map(([c, v]) => (
            <div key={c} style={{ display: 'grid', gridTemplateColumns: '110px 1fr 96px', alignItems: 'center', gap: 8, fontSize: 12.5, marginBottom: 6 }}>
              <span style={{ color: '#4B5563' }}>{c}</span>
              <span style={{ height: 8, background: '#EFF4FE', borderRadius: 4 }}><span style={{ display: 'block', height: 8, width: `${(v / max) * 100}%`, background: '#2563EB', borderRadius: 4 }} /></span>
              <span className="tnum" style={{ textAlign: 'right', fontWeight: 600 }}>{won(v)}</span>
            </div>
          )))}
          {panel('인원별', byPerson.map(([n, v]) => (
            <div key={n} style={{ display: 'flex', fontSize: 12.5, padding: '4px 0', borderBottom: '1px solid #F6F7F9' }}>
              <span style={{ color: '#4B5563' }}>{n}</span><span className="tnum" style={{ marginLeft: 'auto', fontWeight: 600 }}>{won(v)}</span>
            </div>
          )))}
        </div>
      )}
      <ClaimList items={list} loading={loading} showOwner empty="조건에 맞는 청구서가 없습니다." onOpen={onOpen} />
    </>
  );
}

const input: CSSProperties = { width: '100%', boxSizing: 'border-box', height: 38 };
const Field = ({ label, req, children }: { label: string; req?: boolean; children: ReactNode }) => (
  <label style={{ display: 'block', minWidth: 0 }}>
    <span className="dx-field-label">{label}{req && <span style={{ color: '#DC2626' }}> *</span>}</span>{children}
  </label>
);
const Section = ({ title, children }: { title: ReactNode; children: ReactNode }) => (
  <section style={{ marginTop: 22 }}>
    <div className="dx-sec-title">{title}</div>
    {children}
  </section>
);
const count = (n: number, unit: string) => <span style={{ fontSize: 12, fontWeight: 500, color: '#9CA3AF' }}>{n}{unit}</span>;
/** 합계 표시 — 개인(환급) / 법인카드 / 합계 */
const Totals = ({ personal, card, total }: { personal: number; card: number; total: number }) => (
  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
    {([['개인(환급)', personal, '#374151', '#F3F4F6'], ['법인카드', card, '#334155', '#EEF2F6']] as const).map(([k, v, fg, bg]) => (
      <span key={k} className="tnum" style={{ background: bg, color: fg, borderRadius: 8, padding: '6px 10px', fontSize: 12.5 }}>{k} <b>{won(v)}</b></span>
    ))}
    <span className="tnum" style={{ background: '#EFF4FE', color: '#1D4ED8', borderRadius: 8, padding: '5px 12px', fontSize: 13 }}>합계 <b style={{ fontSize: 15 }}>{won(total)}</b></span>
  </div>
);
const newLine = (date = todayKst()): ExpenseLine => ({ date, category: '식대', method: '개인', amount: 0, vendor: '', memo: '' });
export const tripText = (t: BusinessTrip) => `${md(t.startDate)}${t.endDate !== t.startDate ? `~${md(t.endDate)}` : ''} ${t.location}`;

/**
 * 청구서 작성·조회 팝업 — 본인 청구서가 작성중·반려면 편집, 그 외는 조회(+권한별 결재 버튼).
 * 출장 화면에서는 trip 을 넘겨 그 출장에 연결된 새 청구서로 시작한다.
 */
export function ExpenseModal({ id: initialId, trip, onClose }: { id?: string; trip?: BusinessTrip; onClose: () => void }): JSX.Element {
  const { me, flash } = useApp();
  const qc = useQueryClient();
  const mobile = useIsMobile();
  const [id, setId] = useState(initialId);
  const q = useQuery({ queryKey: ['expense', id], queryFn: () => expenseApi.get(id!), enabled: !!id });
  const trips = useQuery({ queryKey: ['trips', 'mine'], queryFn: () => api.tripsMine() });
  const claim = q.data?.claim;
  const files = q.data?.files ?? [];
  const isOwner = !id || (!!claim && claim.owner.toLowerCase() === me.upn.toLowerCase());
  const editing = isOwner && (!id || (!!claim && (claim.status === 'draft' || claim.status === 'rejected')));
  const ceo = me.roles.includes('CEO');

  const [title, setTitle] = useState(trip ? `출장 경비 - ${trip.location}` : '');
  const [project, setProject] = useState('');
  const [tripId, setTripId] = useState(trip?.id ?? '');
  const [lines, setLines] = useState<ExpenseLine[]>([newLine(trip?.startDate)]);
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [busy, setBusy] = useState<string | null>(null);
  const [rejecting, setRejecting] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const loaded = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (!claim || loaded.current === claim.id) return;
    loaded.current = claim.id;
    setTitle(claim.title); setProject(claim.project); setTripId(claim.tripId ?? '');
    setLines(claim.lines.length ? claim.lines : [newLine()]);
  }, [claim]);

  const totals = expenseTotals(lines);
  const refresh = async () => { await qc.invalidateQueries({ queryKey: ['expenses'] }); await qc.invalidateQueries({ queryKey: ['expense', id] }); };
  const run = async (labelText: string, fn: () => Promise<void>) => {
    setBusy(labelText);
    try { await fn(); } catch (e) { flash(errMsg(e, '처리 실패')); } finally { setBusy(null); }
  };
  const setLine = (i: number, patch: Partial<ExpenseLine>) => setLines((ls) => ls.map((l, j) => (j === i ? { ...l, ...patch } : l)));

  /** 저장(+대기 중 첨부 업로드) 후 청구번호 반환 */
  const persist = async (): Promise<string> => {
    if (!title.trim()) throw new Error('제목을 입력하세요.');
    const { claim: saved } = await expenseApi.save({ id, title: title.trim(), project: project.trim(), tripId: tripId || undefined, lines: lines.filter((l) => l.amount > 0 || l.vendor || l.memo) });
    for (const f of pendingFiles) {
      if (f.size > MAX_BYTES) { flash(`${f.name}: 50MB 초과로 제외`); continue; }
      await expenseApi.upload(saved.id, f.name, await readB64(f), f.type || 'application/octet-stream');
    }
    setPendingFiles([]);
    loaded.current = saved.id;
    setId(saved.id);
    return saved.id;
  };
  const saveDraft = () => run('저장 중…', async () => { await persist(); flash('임시 저장했습니다'); await refresh(); });
  const submit = () => run('제출 중…', async () => {
    const cid = await persist();
    await expenseApi.act(cid, 'submit');
    flash('제출했습니다. CEO 결재를 기다립니다.');
    await refresh(); onClose();
  });
  const act = (a: 'withdraw' | 'approve' | 'reject' | 'pay', reason?: string) => run('처리 중…', async () => {
    await expenseApi.act(id!, a, reason);
    flash({ withdraw: '회수했습니다. 수정 후 다시 제출하세요.', approve: '승인했습니다', reject: '반려했습니다', pay: '지급 완료로 처리했습니다' }[a]);
    setRejecting(null);
    await refresh();
    if (a !== 'withdraw') onClose();
  });

  const lineCols = mobile ? '1fr 1fr' : '138px 140px 104px minmax(0,1fr) minmax(0,1.3fr) 122px 32px';
  const rejectBox = (suffix = '') => (
    <div style={{ display: 'flex', gap: 10, background: '#FEF2F2', border: '1px solid #FECACA', color: '#991B1B', fontSize: 12.5, borderRadius: 10, padding: '10px 12px', lineHeight: 1.55, marginBottom: 16 }}>
      <b style={{ flexShrink: 0 }}>반려 사유</b><span>{claim?.rejectReason}{suffix}</span>
    </div>
  );

  let body: ReactNode;
  if (id && q.isLoading) body = <div style={{ padding: 48, textAlign: 'center' }}><Spinner size={24} /></div>;
  else if (id && !claim) body = <div style={{ padding: 32, textAlign: 'center', color: '#DC2626', fontSize: 13 }}>{q.error ? (q.error as Error).message : '청구서를 찾을 수 없습니다.'}</div>;
  else if (editing) {
    const tripOptions = (trips.data?.items ?? []).filter((t) => t.status !== '취소' || t.id === tripId);
    body = (
      <>
        {claim?.status === 'rejected' && rejectBox(' — 수정 후 다시 제출하세요.')}
        <div style={{ display: 'grid', gridTemplateColumns: mobile ? '1fr' : '2fr 1.3fr 1.3fr', gap: 14 }}>
          <Field label="제목" req><input className="dx-input" style={input} value={title} onChange={(e) => setTitle(e.target.value)} placeholder="예) 10월 고객사 미팅 경비" /></Field>
          <Field label="관련 프로젝트·고객사"><input className="dx-input" style={input} value={project} onChange={(e) => setProject(e.target.value)} placeholder="직접 입력" /></Field>
          <Field label="연결 출장">
            <select className="dx-select" style={input} value={tripId} onChange={(e) => setTripId(e.target.value)}>
              <option value="">없음</option>
              {tripOptions.map((t) => <option key={t.id} value={t.id}>{tripText(t)}{t.status === '취소' ? ' (취소)' : ''}</option>)}
            </select>
          </Field>
        </div>

        <Section title={<>사용 내역 <span style={{ color: '#DC2626' }}>*</span>{count(lines.length, '건')}</>}>
          <div style={{ background: '#F9FAFB', border: '1px solid #EEF0F3', borderRadius: 12, padding: mobile ? 10 : '10px 12px 12px' }}>
            {!mobile && (
              <div style={{ display: 'grid', gridTemplateColumns: lineCols, gap: 8, fontSize: 11.5, fontWeight: 700, color: '#6B7280', padding: '2px 2px 8px' }}>
                <span>사용일</span><span>분류</span><span>결제</span><span>사용처</span><span>내용(목적·참석자)</span><span style={{ textAlign: 'right' }}>금액(원)</span><span />
              </div>
            )}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {lines.map((l, i) => (
                <div key={i} style={{ display: 'grid', gridTemplateColumns: lineCols, gap: 8, alignItems: 'center', ...(mobile ? { background: '#fff', border: '1px solid #EEF0F3', borderRadius: 10, padding: 10 } : {}) }}>
                  <input type="date" className="dx-input" style={input} value={l.date} onChange={(e) => setLine(i, { date: e.target.value })} aria-label="사용일" />
                  <select className="dx-select" style={input} value={l.category} onChange={(e) => setLine(i, { category: e.target.value as ExpenseCategory })} aria-label="분류">
                    {EXPENSE_CATEGORIES.map((c) => <option key={c}>{c}</option>)}
                  </select>
                  <select className="dx-select" style={{ ...input, color: l.method === '법인카드' ? '#334155' : undefined, fontWeight: 600 }} value={l.method} onChange={(e) => setLine(i, { method: e.target.value as ExpenseLine['method'] })} aria-label="결제수단">
                    {PAY_METHODS.map((m) => <option key={m}>{m}</option>)}
                  </select>
                  <input className="dx-input" style={input} value={l.vendor} onChange={(e) => setLine(i, { vendor: e.target.value })} placeholder="사용처" aria-label="사용처" />
                  <input className="dx-input" style={{ ...input, ...(mobile ? { gridColumn: '1 / -1' } : {}) }} value={l.memo} onChange={(e) => setLine(i, { memo: e.target.value })} placeholder="목적·참석자" aria-label="내용" />
                  <input inputMode="numeric" className="dx-input tnum" style={{ ...input, textAlign: 'right', fontWeight: 700 }} aria-label="금액"
                    value={l.amount ? l.amount.toLocaleString('ko-KR') : ''} placeholder="0"
                    onChange={(e) => setLine(i, { amount: Math.min(1_000_000_000, Number(e.target.value.replace(/\D/g, '')) || 0) })} />
                  <IconX label="이 줄 삭제" disabled={lines.length === 1} onClick={() => setLines((ls) => ls.filter((_, j) => j !== i))} />
                </div>
              ))}
            </div>
            <button onClick={() => setLines((ls) => [...ls, newLine(ls[ls.length - 1]?.date)])}
              style={{ marginTop: 10, width: '100%', height: 38, border: '1px dashed #C7D2FE', borderRadius: 8, background: '#fff', color: '#2563EB', fontFamily: 'inherit', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>+ 사용 내역 추가</button>
          </div>
          <div style={{ marginTop: 12 }}><Totals {...totals} /></div>
        </Section>

        <Section title={<>영수증·증빙 첨부 {count(files.length + pendingFiles.length, '개')}</>}>
          <FilesBox id={id} files={files} pending={pendingFiles} editable
            onAdd={(fs) => setPendingFiles((p) => [...p, ...fs])} onRemovePending={(i) => setPendingFiles((p) => p.filter((_, j) => j !== i))}
            onRemoveSaved={(fid) => void run('삭제 중…', async () => { await expenseApi.removeFile(id!, fid); await refresh(); })} />
        </Section>
      </>
    );
  } else if (claim) {
    const rows: Array<[string, string]> = [
      ['작성자', `${claim.ownerName}${claim.dept ? ` · ${claim.dept}` : ''}`],
      ['관련 프로젝트', claim.project || '—'],
      ['연결 출장', claim.tripLabel || '—'],
      ['제출', dt(claim.submittedAt) || '—'],
      ['결재', claim.decidedAt ? `${dt(claim.decidedAt)} ${claim.decidedByName ?? ''}` : '—'],
      ...(claim.personal ? [['지급', dt(claim.paidAt) || '—'] as [string, string]] : []),
    ];
    const th: CSSProperties = { padding: '9px 12px', fontWeight: 700, fontSize: 11.5, color: '#6B7280', textAlign: 'left', whiteSpace: 'nowrap' };
    const td: CSSProperties = { padding: '9px 12px', borderTop: '1px solid #F3F4F6' };
    body = (
      <>
        {claim.status === 'rejected' && rejectBox()}
        <div style={{ display: 'grid', gridTemplateColumns: mobile ? '1fr 1fr' : 'repeat(3, 1fr)', gap: 1, background: '#EEF0F3', border: '1px solid #EEF0F3', borderRadius: 12, overflow: 'hidden' }}>
          {rows.map(([k, v]) => (
            <div key={k} style={{ background: '#fff', padding: '10px 14px' }}>
              <div style={{ fontSize: 11.5, color: '#9CA3AF' }}>{k}</div>
              <div style={{ fontSize: 13, marginTop: 3, fontWeight: 600, color: '#1F2937' }}>{v}</div>
            </div>
          ))}
        </div>

        <Section title={<>사용 내역 {count(claim.lines.length, '건')}</>}>
          <div style={{ border: '1px solid #EEF0F3', borderRadius: 12, overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, minWidth: 600 }}>
              <thead><tr style={{ background: '#F9FAFB' }}>{['사용일', '분류', '결제', '사용처', '내용', '금액'].map((h) => <th key={h} style={{ ...th, textAlign: h === '금액' ? 'right' : 'left' }}>{h}</th>)}</tr></thead>
              <tbody>
                {claim.lines.map((l, i) => (
                  <tr key={i}>
                    <td style={{ ...td, whiteSpace: 'nowrap', color: '#4B5563' }}>{l.date}</td><td style={td}>{l.category}</td>
                    <td style={td}><span style={{ fontSize: 11.5, fontWeight: 700, padding: '2px 8px', borderRadius: 6, ...(l.method === '법인카드' ? { background: '#EEF2F6', color: '#334155' } : { background: '#F3F4F6', color: '#374151' }) }}>{l.method}</span></td>
                    <td style={td}>{l.vendor}</td><td style={{ ...td, color: '#4B5563' }}>{l.memo}</td>
                    <td className="tnum" style={{ ...td, textAlign: 'right', fontWeight: 700, whiteSpace: 'nowrap' }}>{won(l.amount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div style={{ marginTop: 12 }}><Totals personal={claim.personal} card={claim.card} total={claim.total} /></div>
        </Section>

        <Section title={<>영수증·증빙 {count(files.length, '개')}</>}>
          <FilesBox id={id} files={files} pending={[]} />
        </Section>

        {rejecting !== null && (
          <Section title="반려 사유">
            <textarea autoFocus className="dx-textarea" value={rejecting} onChange={(e) => setRejecting(e.target.value)} rows={3} placeholder="작성자에게 전달됩니다" />
          </Section>
        )}
      </>
    );
  }

  // 바닥글 — 왼쪽: 삭제·안내·진행 표시, 오른쪽: 닫기 + 주요 동작(가장 오른쪽이 기본 동작)
  const btn = (text: string, onClick: () => void, cls = 'dx-btn-ghost', disabled = false) => (
    <button key={text} className={`dx-btn ${cls}`} disabled={!!busy || disabled} onClick={onClick}>{text}</button>
  );
  const left: ReactNode[] = [];
  const right: ReactNode[] = [btn('닫기', onClose)];
  if (editing) {
    if (id) left.push(btn('삭제', () => setConfirmDelete(true), 'dx-btn-danger-outline'));
    if (totals.total <= 0 && !busy) left.push(<span key="hint" style={{ fontSize: 12, color: '#9CA3AF' }}>금액을 입력하면 제출할 수 있습니다</span>);
    right.push(btn('임시 저장', saveDraft), btn('제출', submit, 'dx-btn-primary', totals.total <= 0));
  } else if (claim) {
    if (isOwner && claim.status === 'submitted') right.push(btn('회수', () => act('withdraw')));
    if (ceo && claim.status === 'submitted') {
      right.push(rejecting === null
        ? btn('반려', () => setRejecting(''), 'dx-btn-danger-outline')
        : btn('반려 확정', () => act('reject', rejecting), 'dx-btn-danger', !rejecting.trim()));
      right.push(btn(claim.personal ? `승인 · 환급 ${won(claim.personal)}` : '승인', () => act('approve'), 'dx-btn-primary'));
    }
    if (ceo && claim.status === 'approved') right.push(btn(`지급 완료 · ${won(claim.personal)}`, () => act('pay'), 'dx-btn-primary'));
  }

  return (
    <ModalFrame width={mobile ? 460 : 1020} onClose={onClose}
      title={editing ? (id ? '경비 청구 수정' : '새 경비 청구') : claim?.title ?? '경비 청구'}
      extra={claim && <ExpenseBadge status={claim.status} />}
      sub={id ? `청구번호 ${id}` : '개인 지출(환급)과 법인카드 사용을 한 청구서에 함께 적을 수 있습니다. 임시 저장 후 이어서 작성할 수 있습니다.'}
      footer={<>
        {left}
        {busy && <span style={{ fontSize: 12, color: '#2563EB', display: 'inline-flex', alignItems: 'center', gap: 6 }}><Spinner size={14} />{busy}</span>}
        <span style={{ flex: 1 }} />
        {right}
      </>}>
      {body}
      {confirmDelete && (
        <ConfirmDialog title="청구서 삭제" message="이 청구서와 첨부 파일을 모두 삭제할까요?" confirmLabel="삭제" danger
          onConfirm={() => { setConfirmDelete(false); void run('삭제 중…', async () => { await expenseApi.remove(id!); flash('삭제했습니다'); await refresh(); onClose(); }); }}
          onCancel={() => setConfirmDelete(false)} />
      )}
    </ModalFrame>
  );
}

/** 작은 × 버튼 — 평소 회색, 마우스 올리면 빨강 */
function IconX({ label, disabled, onClick }: { label: string; disabled?: boolean; onClick: () => void }): JSX.Element {
  const [hover, setHover] = useState(false);
  const on = hover && !disabled;
  return (
    <button type="button" title={label} aria-label={label} disabled={disabled} onClick={onClick}
      onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}
      style={{ width: 30, height: 30, border: 'none', borderRadius: 8, background: on ? '#FEF2F2' : 'transparent', color: disabled ? '#D1D5DB' : on ? '#DC2626' : '#9CA3AF', fontSize: 17, cursor: disabled ? 'default' : 'pointer', flexShrink: 0 }}>×</button>
  );
}

const FILE_TINT: Record<string, [string, string]> = {
  PDF: ['#FEF2F2', '#DC2626'], JPG: ['#ECFDF5', '#059669'], JPEG: ['#ECFDF5', '#059669'], PNG: ['#ECFDF5', '#059669'], HEIC: ['#ECFDF5', '#059669'],
  XLSX: ['#F0FDF4', '#16A34A'], XLS: ['#F0FDF4', '#16A34A'],
};

/** 영수증·증빙 첨부 — 저장 전 선택한 파일은 저장 시 함께 올라감 */
function FilesBox({ id, files, pending, editable, onAdd, onRemovePending, onRemoveSaved }: {
  id?: string; files: { id: string; name: string; size: number }[]; pending: File[]; editable?: boolean;
  onAdd?: (f: File[]) => void; onRemovePending?: (i: number) => void; onRemoveSaved?: (fileId: string) => void;
}): JSX.Element {
  const { flash } = useApp();
  const [drag, setDrag] = useState(false);
  const size = (n: number) => (n < 1024 * 1024 ? `${Math.max(1, Math.ceil(n / 1024))} KB` : `${(n / 1024 / 1024).toFixed(1)} MB`);
  const item = (key: string, name: string, meta: ReactNode, onX?: () => void, onOpen?: () => void) => {
    const ext = (name.split('.').pop() ?? '').toUpperCase().slice(0, 4);
    const [bg, fg] = FILE_TINT[ext] ?? ['#EEF2F6', '#475569'];
    const nameStyle: CSSProperties = { fontSize: 13, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block', maxWidth: '100%' };
    return (
      <div key={key} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px', border: '1px solid #EEF0F3', borderRadius: 10, background: '#fff' }}>
        <span style={{ width: 34, height: 34, borderRadius: 8, background: bg, color: fg, fontSize: 10, fontWeight: 800, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>{ext || '파일'}</span>
        <div style={{ flex: 1, minWidth: 0 }}>
          {onOpen
            ? <button onClick={onOpen} title="내려받기" style={{ ...nameStyle, border: 'none', background: 'transparent', padding: 0, fontFamily: 'inherit', color: '#1D4ED8', cursor: 'pointer', textAlign: 'left' }}>{name}</button>
            : <span style={nameStyle}>{name}</span>}
          <div style={{ fontSize: 11.5, color: '#9CA3AF' }}>{meta}</div>
        </div>
        {onX && <IconX label={`${name} 삭제`} onClick={onX} />}
      </div>
    );
  };
  const list = [
    ...files.map((f) => item(f.id, f.name, size(f.size), editable ? () => onRemoveSaved?.(f.id) : undefined,
      () => void expenseApi.download(id!, f).catch((e) => flash(errMsg(e, '다운로드 실패'))))),
    ...pending.map((f, i) => item(`p${i}`, f.name, <>{size(f.size)} · <span style={{ color: '#D97706' }}>저장 시 업로드</span></>, () => onRemovePending?.(i))),
  ];
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {list.length > 0 && <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 8 }}>{list}</div>}
      {!editable && !files.length && <div style={{ fontSize: 12.5, color: '#9CA3AF' }}>첨부 없음</div>}
      {editable && (
        <label onDragOver={(e) => { e.preventDefault(); setDrag(true); }} onDragLeave={() => setDrag(false)}
          onDrop={(e) => { e.preventDefault(); setDrag(false); if (e.dataTransfer.files.length) onAdd?.(Array.from(e.dataTransfer.files)); }}
          style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12, border: `1.5px dashed ${drag ? '#2563EB' : '#CBD5E1'}`, background: drag ? '#EFF6FF' : '#FAFBFC', borderRadius: 12, padding: '16px 14px', cursor: 'pointer', transition: 'all .15s' }}>
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke={drag ? '#2563EB' : '#94A3B8'} strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden><path d="M12 16V4M7 9l5-5 5 5" /><path d="M4 15v3a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-3" /></svg>
          <span style={{ textAlign: 'left' }}>
            <span style={{ display: 'block', fontSize: 13, fontWeight: 700, color: drag ? '#2563EB' : '#374151' }}>영수증 사진·PDF를 끌어다 놓거나 <span style={{ color: '#2563EB' }}>파일 선택</span></span>
            <span style={{ display: 'block', fontSize: 11.5, color: '#9CA3AF', marginTop: 2 }}>휴대폰은 바로 촬영 가능 · 1개 최대 50MB · 여러 개 가능</span>
          </span>
          <input type="file" hidden multiple accept="image/*,.pdf,.xlsx,.xls,.hwp,.hwpx,.doc,.docx,.zip"
            onChange={(e) => { if (e.target.files?.length) onAdd?.(Array.from(e.target.files)); e.target.value = ''; }} />
        </label>
      )}
    </div>
  );
}
